import { i18n as linguiI18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { useEffect, useState } from 'react';
import { getUserPreference, setUserPreference } from '../db';
import { IS_PROD } from '../config/env';
// Bundle locale catalogs to avoid runtime chunk fetch failures on stale deployed clients.
import { messages as enMessages } from '../../locales/en/messages';
import { messages as esMessages } from '../../locales/es/messages';
import { messages as frMessages } from '../../locales/fr/messages';

// Initialize i18n instance
const i18n = linguiI18n;

// Synchronously initialize with English as default to prevent errors
i18n.load('en', enMessages);
i18n.activate('en');

const localeMessages = {
  en: enMessages,
  es: esMessages,
  fr: frMessages,
};

export type Locale = keyof typeof localeMessages;

function isSupportedLocale(value: unknown): value is Locale {
  return (
    typeof value === 'string' &&
    Object.prototype.hasOwnProperty.call(localeMessages, value)
  );
}

function localeBreadcrumb(event: string, details: Record<string, unknown>) {
  if (!IS_PROD) return;

  const payload = {
    event,
    ...details,
    hostname: typeof window !== 'undefined' ? window.location.hostname : 'unknown',
  };

  console.info('[i18n]', payload);
}

export async function activateLocale(locale: Locale) {
  localeBreadcrumb('activateLocale:start', { targetLocale: locale, currentLocale: i18n.locale || 'en' });

  const messages = localeMessages[locale] ?? enMessages;
  i18n.load(locale, messages);
  i18n.activate(locale);

  localeBreadcrumb('activateLocale:success', { activeLocale: i18n.locale || locale });

  if (typeof window !== 'undefined') {
    localStorage.setItem('locale', locale);
  }

  // Keep locale persistence best-effort so UI language switches even if IndexedDB is unavailable.
  try {
    await setUserPreference('locale', locale);
  } catch (error) {
    console.warn('Failed to persist locale preference to IndexedDB:', error);
  }
}

export async function initI18n() {
  // Try to get locale from localStorage first (faster)
  let locale: Locale = 'en';
  
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem('locale');
    if (isSupportedLocale(stored)) {
      locale = stored;
    }
  }
  
  // Also check Dexie
  try {
    const dbLocale = await getUserPreference('locale');
    if (isSupportedLocale(dbLocale)) {
      locale = dbLocale;
    }
  } catch (error) {
    console.warn('Failed to read locale preference from IndexedDB:', error);
  }
  
  // Only load/activate if different from default
  if (locale !== 'en') {
    await activateLocale(locale);
  } else {
    localeBreadcrumb('initI18n:default-locale', { activeLocale: 'en' });
  }

  localeBreadcrumb('initI18n:complete', { activeLocale: i18n.locale || locale });

  return locale;
}

export function useLocale() {
  const [locale, setLocaleState] = useState<Locale>((i18n.locale || 'en') as Locale);
  
  useEffect(() => {
    const unsubscribe = i18n.on('change', () => {
      setLocaleState((i18n.locale || 'en') as Locale);
    });

    return () => {
      unsubscribe();
    };
  }, []);
  
  const changeLocale = async (newLocale: Locale) => {
    try {
      await activateLocale(newLocale);
      setLocaleState(newLocale);
    } catch (error) {
      localeBreadcrumb('changeLocale:error', {
        targetLocale: newLocale,
        error: error instanceof Error ? error.message : String(error),
      });
      console.error('Failed to activate locale:', error);
    }
  };
  
  return { locale, changeLocale };
}

export { i18n, I18nProvider };
